import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { App as AntApp, Button, Layout, Spin, Result, ConfigProvider } from 'antd'
import thTH from 'antd/locale/th_TH'
import dayjs from 'dayjs'
import 'dayjs/locale/th'


import { useAuth } from './contexts/AuthContext'
import api from './utils/api'
import LoginPage from './pages/Auth/LoginPage'
import DashboardLayout from './components/Layout/DashboardLayout'
import Dashboard from './pages/Dashboard/DashboardWithQuote'
import ProductsPage from './pages/Products/ProductsPage'
import ContactsPage from './pages/Contacts/ContactsPage'
//import QuotesPage from './pages/Quotes/QuotesPage'
import QuotesPage from './pages/Quotes/QuotePageWithVatUnit'
import ReceiptsPage from './pages/Receipts/ReceiptsPage'
import ExpensesPage from './pages/Expenses/ExpensesPage'
import CalendarPage from './pages/Calendar/CalendarPage'
import NotesPage from './pages/Notes/NotesPage'
import ReportsPage from './pages/Reports/ReportsPage'
import UsersPage from './pages/Users/UsersPage'
import UploadPage from './pages/Upload/UploadPage'
import CompanySettingsPage from './pages/Settings/CompanySettingsPage'
import PurchaseManagement from './pages/Purchase/PurchaseManagement'
import CreatePurchaseOrder from './pages/Purchase/CreatePurchaseOrderPage';
import CreateReceiptPage from './pages/Purchase/CreateReceiptPage';
import GoodsReceiptsManagement from './pages/Purchase/GoodsReceiptsManagement';
import SuppliersManagement from './pages/Purchase/SuppliersManagement';
import PurchaseOrderDetails from './pages/Purchase/PurchaseOrderDetails';
import ReceiptsPageWithBarcode from './pages/Receipts/ReceiptsPageWithBarcode'
import POSPage from './pages/Receipts/POSPage'
import DeliveryNotesPage from './pages/DeliveryNotes/DeliveryNotesPage';
import PendingDeliveriesPage from './pages/PendingDeliveries/PendingDeliveriesPage_Complete'
import ApiSettingsPage from './pages/Settings/ApiSettingsPage';
import ApiDocumentationPage from './pages/Settings/ApiDocumentationPage';
import UnitsManagementPage from './pages/Units/UnitsManagementPage';
import ProductCategoriesManagementPage from './pages/Products/ProductCategoriesManagementPage';
import StorageSettingsWizard from './pages/Settings/StorageSettingsWizard';
import AccessControlPage from './pages/Settings/AccessControlPage';
import AISettingsPage from './pages/Settings/AISettingsPage';
import LineIntegrationPage from './pages/Settings/LineIntegrationPage';
import ExpenseImportReviewPage from './pages/Expenses/ExpenseImportReviewPage';

import ProjectsListPage from './pages/Projects/ProjectsListPage'
import CreateProjectPage from './pages/Projects/CreateProjectPage'
import EditProjectPage from './pages/Projects/EditProjectPage'
import ProjectTasksPage from './pages/Projects/ProjectTasksPage'
import TasksPage from './pages/Task/TasksPage'
import ProjectDetail from './pages/Projects/ProjectDetail'
import DirectorAdvancePage from './pages/DirectorAdvance/DirectorAdvancePage'
import DirectorAdvanceReport from './pages/DirectorAdvance/DirectorAdvanceReport'
import BankStatementImportPage from './pages/BankStatement/BankStatementImportPage'
import ChartOfAccountsPage from './pages/Accounting/ChartOfAccountsPage'
import BankAccountsPage from './pages/Accounting/BankAccountsPage'
import JournalEntriesPage from './pages/Accounting/JournalEntriesPage'
import TransfersPage from './pages/Accounting/TransfersPage'
import BankDepositsPage from './pages/Accounting/BankDepositsPage'
import AccountingSettingsPage from './pages/Accounting/AccountingSettingsPage'
import AccountingReportsPage from './pages/Accounting/AccountingReportsPage'
import BulkPostingPage from './pages/Accounting/BulkPostingPage'
import AccountingDangerZonePage from './pages/Accounting/AccountingDangerZonePage'
import MaterialsPage from './pages/Materials/MaterialsPage'
import InventoryPage from './pages/Inventory/InventoryPage'
import SalesReturnsPage from './pages/Inventory/SalesReturnsPage'
import WarrantyPage from './pages/Warranty/WarrantyPage'
import InstallationPage from './pages/Installation/InstallationPage'
import PrintEstimatesPage from './pages/PrintEstimates/PrintEstimatesPage'
import CustomerEstimatesPage from './pages/PrintEstimates/CustomerEstimatesPage'
import InvestorDashboardPage from './pages/Investors/InvestorDashboardPage'
import InvestorAdminPage from './pages/Investors/InvestorAdminPage'


dayjs.locale('th')

// Page views are deliberately sent without query parameters. The server accepts
// only this event type and stores it in the immutable user-audit ledger.
const AuditPageTracker = () => {
  const location = useLocation()
  useEffect(() => {
    if (!localStorage.getItem('token')) return
    let sessionId = sessionStorage.getItem('user_audit_session_id')
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      sessionStorage.setItem('user_audit_session_id', sessionId)
    }
    api.post('/api/user-audit/client-events', { event_type: 'page_view', path: location.pathname, session_id: sessionId }).catch(() => {})
  }, [location.pathname])
  return null
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="กำลังโหลด..." />
      </Layout>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.role === 'INVESTOR' && !window.location.pathname.startsWith('/investor-dashboard')) {
    return <Navigate to="/investor-dashboard" replace />
  }
  return children
}

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {

  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="กำลังโหลด..." />
      </Layout>
    )
  }

  if (user) {
    return <Navigate to={user.role === 'INVESTOR' ? '/investor-dashboard' : '/dashboard'} replace />
  }

  return children
}

function App() {
  const [installation,setInstallation] = useState({ loading:true, data:null, error:null })

  const loadInstallationStatus = () => {
    setInstallation(current=>({ ...current, loading:true, error:null }))
    api.get('/api/installation/status', { timeout:15000 })
      .then(response=>setInstallation({ loading:false, data:response.data.data, error:null }))
      .catch(error=>setInstallation({ loading:false, data:null, error:error.response?.data?.message || error.message }))
  }

  useEffect(() => { loadInstallationStatus() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (installation.loading) return <Layout style={{minHeight:'100vh',display:'grid',placeItems:'center'}}><Spin size="large" tip="กำลังตรวจสอบระบบ..."/></Layout>
  if (installation.error) return <Result status="warning" title="ไม่สามารถตรวจสอบสถานะการติดตั้ง"
    subTitle={installation.error} extra={<Button type="primary" onClick={loadInstallationStatus}>ลองใหม่</Button>} />
  if (installation.data?.installation_required) return <ConfigProvider locale={thTH}><AntApp><Routes>
    <Route path="/install" element={<InstallationPage status={installation.data} onInstalled={loadInstallationStatus}/>}/>
    <Route path="*" element={<Navigate to="/install" replace/>}/>
  </Routes></AntApp></ConfigProvider>

  return (
    <ConfigProvider locale={thTH}>
      <AntApp>
      <AuditPageTracker />
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/customer-estimates/*" element={<CustomerEstimatesPage />} />
          <Route path="/install" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route path="/investor-dashboard" element={<ProtectedRoute><InvestorDashboardPage /></ProtectedRoute>} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/investors" element={<InvestorAdminPage />} />
                    <Route path="/products/*" element={<ProductsPage />} />
                    <Route path="/materials" element={<MaterialsPage />} />
                    <Route path="/inventory" element={<InventoryPage />} />
                    <Route path="/sales-returns" element={<SalesReturnsPage />} />
                    <Route path="/warranties" element={<WarrantyPage />} />
                    <Route path="/contacts/*" element={<ContactsPage />} />
                    <Route path="/quotes/*" element={<QuotesPage />} />
                    <Route path="/print-estimates/*" element={<PrintEstimatesPage />} />
                    <Route path="/delivery-notes/*" element={<DeliveryNotesPage />} />
                    <Route path="/delivery-pending/*" element={<PendingDeliveriesPage />} />
                    <Route path="/receipts/*" element={<ReceiptsPageWithBarcode />} />
                    {/* <Route path="/receipts/barcode" element={<ReceiptsPageWithBarcode />} /> */}
                    <Route path="/receipts/pos" element={<POSPage />} />
                    <Route path="/expenses/*" element={<ExpensesPage />} />
                    <Route path="/expenses/import-review" element={<ExpenseImportReviewPage />} />

                    <Route path="/projects" element={<ProjectsListPage />} />
                    <Route path="/projects/create" element={<CreateProjectPage />} />
                    <Route path="/projects/edit/:projectId" element={<EditProjectPage />} />
                    <Route path="/projects/:projectId/tasks" element={<ProjectDetail />} />
                    <Route path="/projects/:projectId/kanban" element={<ProjectTasksPage />} />

                    <Route path="/director-advance" element={<DirectorAdvancePage />} />
                    <Route path="/director-advance/report" element={<DirectorAdvanceReport />} />
                    <Route path="/bank-statement-import" element={<BankStatementImportPage />} />

                    {/* Accounting System */}
                    <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
                    <Route path="/accounting/bank-accounts" element={<BankAccountsPage />} />
                    <Route path="/accounting/journal-entries" element={<JournalEntriesPage />} />
                    <Route path="/accounting/transfers" element={<TransfersPage />} />
                    <Route path="/accounting/deposits" element={<BankDepositsPage />} />
                    <Route path="/accounting/settings" element={<AccountingSettingsPage />} />
                    <Route path="/accounting/reports" element={<AccountingReportsPage />} />
                    <Route path="/accounting/bulk-posting" element={<BulkPostingPage />} />
                    <Route path="/accounting/danger-zone" element={<AccountingDangerZonePage />} />


                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/notes" element={<NotesPage />} />
                    <Route path="/reports/*" element={<ReportsPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/settings/company" element={<CompanySettingsPage />} />
                    <Route path="/settings/api" element={<ApiSettingsPage />} />
                    <Route path="/settings/api/docs" element={<ApiDocumentationPage />} />
                    <Route path="/settings/units" element={<UnitsManagementPage />} />
                    <Route path="/settings/product-categories" element={<ProductCategoriesManagementPage />} />
                    <Route path="/settings/storage" element={<StorageSettingsWizard />} />
                    <Route path="/settings/access-control" element={<AccessControlPage />} />
                    <Route path="/settings/ai" element={<AISettingsPage />} />
                    <Route path="/settings/integrations/line" element={<LineIntegrationPage />} />
                    {/* หน้าจัดการใบสั่งซื้อหลัก */}
                    <Route path="/purchases" element={<PurchaseManagement />} />

                    {/* หน้าสร้างใบสั่งซื้อใหม่ */}
                    <Route path="/purchases/create" element={<CreatePurchaseOrder />} />

                    {/* หน้าดูรายละเอียดใบสั่งซื้อ */}
                    <Route path="/purchases/view/:id" element={<PurchaseOrderDetails />} />

                    {/* หน้าแก้ไขใบสั่งซื้อ */}
                    <Route path="/purchases/edit/:id" element={<CreatePurchaseOrder />} />

                    {/* หน้าจัดการผู้จำหน่าย */}
                    <Route path="/purchases/suppliers" element={<SuppliersManagement />} />

                    {/* === Goods Receipts Routes === */}

                    {/* หน้าสร้างใบรับสินค้าเข้าคลัง */}
                    <Route path="/purchases/goods-receipt/create" element={<CreateReceiptPage />} />
                    <Route path="/purchases/goods-receipt/create/:id" element={<CreateReceiptPage />} />
                    <Route path="/purchases/goods-receipts" element={<GoodsReceiptsManagement />} />
                    {/* <Route path="*" element={<Navigate to="/dashboard" replace />} /> */}
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
      </AntApp>
    </ConfigProvider>
  )
}

export default App
